import { ChecklistenEingabe } from '../components/ChecklistenEingabe'
import { useLocation, useNavigate } from 'react-router-dom'
import { aufforderungenNeuLaden } from '../hooks/useAufforderungen'
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { ChecklistZeile } from '../components/ChecklistZeile'
import { PageHeader, Card, Button, Badge, Select, Input, Dialog, Textarea, useEinsatz, useConnectionRetry, useBestaetigung, checklistenAnzeigeName, checklistenFortschritt } from '@pilot-hub/ui'
import { EinsatzChecklistenLesen } from '../components/EinsatzChecklistenLesen'
import { api, flightApi, droneApi, missionApi, checklistenApi, userApi } from '@pilot-hub/api-client'
import { useAuth } from '@pilot-hub/auth'
import { MeinTrupp } from '../components/MeinTrupp'
import { FlugartWahl } from '../components/FlugartWahl'
import { VorkommnisFelder, VorkommnisWert, vorkommnisZuApi, vorkommnisFehler, vorkommnisBemerkungZuApi, useFlugHms } from '../components/VorkommnisFelder'
import { formatLaufzeit, useSekundenTakt } from '../components/FlugStatusChip'
import { useAlleOk } from '../components/AlleOk'
import { akkuAnzeige } from '../utils/akku'
import {
  istVerbindungsfehler, neueUuid, neueQid, lokaleZeit, queueHinzufuegen, queueSenden, beiGesendet,
  lokaleFluege, lokalBeendeteIds, type FlugRef, type StartAnzeige,
} from '../utils/flugQueue'
import { useFlugQueue } from '../components/FlugQueueHinweis'
import { 
  CloudOff, Play, Square, Lock, LockOpen, AlertCircle, Plane, Clock, MapPin, 
  ClipboardList, Check, X, Loader2, ChevronDown, ChevronUp, AlertTriangle, Star, 
  RotateCcw, CheckSquare, Calendar, MessageSquare, Settings, Eye, Edit, Sun, Thermometer, Droplets, Wind, Zap, Compass, Cloud, Gauge 
} from 'lucide-react'

/** Wert eines Zahlenfelds: nur Zahlen anzeigen (ältere Einträge enthalten "true" aus „Alle Pflichtfelder ausfüllen“) */
const zahlWert = (w: string | null | undefined) => (w && Number.isFinite(Number(w)) ? w : '')

interface AktiverFlug {
  id: number
  flug_nr: string
  drohne_label?: string | null
  akku_label?: string | null
  einsatz_titel?: string | null
  einsatz_id?: number | null
  akku_id?: number | null
  startzeit: string | null
  endzeit: string | null
  /** nur lokal vorhanden (Start liegt noch in der Offline-Warteschlange) */
  lokalUuid?: string
}

interface Einsatz { id: number; titel: string; status: string; typ?: string; einsatzort_adresse?: string | null }

interface Akku { id: number; bezeichnung: string; kapazitaet_mah?: number | null; besitzer_label?: string | null }

interface Drohne {
  id: number
  label: string
  asb_inventarnummer: string
  hersteller?: string | null
  modell?: string | null
  besitzer_typ?: string | null
  akkus?: Akku[]
}

/** Start über eine Aufforderung (Drohne eingeschaltet): Drohne und Einsatz stehen fest */
interface StartKontext { drohneId: string; einsatzId: string }

interface ChecklistenItem {
  id: number
  instanz_id: number
  vorlage_item_id: number | null
  text: string
  typ: 'CHECK' | 'TEXT' | 'NUMBER' | 'PHOTO'
  pflicht: boolean
  sortierung: number
  hinweis: string | null
  wert: string | null
  ausgefuellt_am: string | null
  ausgefuellt_von: number | null
}

interface ChecklistenInstanz {
  id: number
  einsatz_id: number
  flug_id: number | null
  vorlage_id: number | null
  name: string
  phase: string
  modul: string
  status: string
  erstellt_am: string
  abgeschlossen_am: string | null
  abgeschlossen_von: number | null
  items: ChecklistenItem[]
}

interface ChecklistenStatus {
  einsatz_beenden_blockiert: boolean
  flug_beenden_blockiert: boolean
  fuehrung_offen: { instanz_id: number; name: string; phase: string; offen: number }[]
  pilot_offen: { instanz_id: number; name: string; phase: string; offen: number }[]
  blocked_reason: string | null
}

const fmtZeit = (s: string | null | undefined) =>
  s ? new Date(s).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' }) : '—'

/** Sonderwert für einen geliehenen (nicht im System erfassten) Akku */
const FREMDAKKU = 'fremd'

const laufzeit = (start: string | null) => {
  if (!start) return '—'
  const min = Math.max(0, Math.floor((Date.now() - new Date(start).getTime()) / 60000))
  const h = Math.floor(min / 60)
  const m = min % 60
  return h > 0 ? `${h} h ${m} min` : `${m} min`
}

/** GPS-Koordinaten des Geräts auslesen */
function holePosition(timeoutMs = 10000): Promise<{ lat: number; lon: number } | null> {
  return new Promise(resolve => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) return resolve(null)
    navigator.geolocation.getCurrentPosition(
      pos => resolve({ lat: pos.coords.latitude, lon: pos.coords.longitude }),
      () => resolve(null),
      { enableHighAccuracy: true, timeout: timeoutMs, maximumAge: 0 }
    )
  })
}

const OFFLINE_HINWEIS = 'Offline gespeichert – wird gesendet, sobald Verbindung besteht.'
const TIMEOUT_MS = 12000
const DROHNEN_CACHE = 'pilot-hub-drohnen-cache'

/** Zuletzt verwendete Auswahl je Benutzer (Vorbelegung „Flug starten“) */
interface Zuletzt { drohneId: string; akkuId: string; flugart: string }
const zuletztKey = (uid: number) => `pilot-hub-flug-zuletzt-${uid}`
function zuletztLesen(uid: number): Zuletzt | null {
  try { const r = localStorage.getItem(zuletztKey(uid)); return r ? (JSON.parse(r) as Zuletzt) : null } catch { return null }
}
function zuletztSchreiben(uid: number, z: Zuletzt) {
  try { localStorage.setItem(zuletztKey(uid), JSON.stringify(z)) } catch { /* egal */ }
}
/** Vorflug-Vorlage je Einsatz zwischenspeichern, damit der Start auch ohne Verbindung mit Checkliste möglich ist */
const STANDARD_FLUGART = 'VLOS'
const vorlageKey = (einsatzId: string) => `pilot-hub-vorflug-vorlage-${einsatzId}`

const coordsText = (p: { lat: number; lon: number }) => `${p.lat.toFixed(6)}, ${p.lon.toFixed(6)}`

// Checklisten Phase Konfiguration für Pilotenmodul
const PILOT_PHASES = [
  { key: 'EINSATZBEREITSCHAFT', label: 'Einsatzbereitschaft', icon: ClipboardList, order: 1 },
  { key: 'EINSATZORT', label: 'Einsatzbereitschaft am Einsatzort', icon: ClipboardList, order: 2 },
  { key: 'VORFLUG', label: 'Vorflugkontrolle', icon: ClipboardList, order: 3 },
  { key: 'START', label: 'Vor dem Start & Flugfunktionen', icon: Play, order: 4 },
  { key: 'LANDUNG', label: 'Vorbereitung der Landung', icon: Square, order: 5 },
  { key: 'NACHFLUG', label: 'Nachflugkontrolle', icon: RotateCcw, order: 6 },
  { key: 'EINSATZENDE_PILOT', label: 'Einsatzende', icon: CheckSquare, order: 7 },
] as const

type PilotPhaseKey = typeof PILOT_PHASES[number]['key']

export function FlightCapturePage() {
  const { user } = useAuth()

  const [aktiveFluege, setAktiveFluege] = useState<AktiverFlug[]>([])
  const [drohnen, setDrohnen] = useState<Drohne[]>([])
  const [loading, setLoading] = useState(true)
  const [fehler, setFehler] = useState<string | null>(null)
  const [startet, setStartet] = useState(false)
  const [beendetId, setBeendetId] = useState<number | null>(null)
  const [gpsHinweis, setGpsHinweis] = useState<string | null>(null)
  const [offlineHinweis, setOfflineHinweis] = useState<string | null>(null)
  const { bestaetigen, dialog: bestaetigungsDialog } = useBestaetigung()
  const { frage: frageAlleOk, dialog: alleOkDialog } = useAlleOk()
  const queue = useFlugQueue()
  const meineQueue = queue.filter(e => e.nutzerId === user?.id)
  const [vorbelegt, setVorbelegt] = useState({ drohne: false, akku: false, flugart: false })
  const [vorbelegenTick, setVorbelegenTick] = useState(0)
  const [abschlussUuid, setAbschlussUuid] = useState<string | null>(null)

  // Formular
  // Globale Einsatzwahl = Vorbelegung. Der Einsatz bleibt ein Attribut des einzelnen Fluges:
  // entsperrt kann er hier pro Flug abweichen (einsatzLokal), ohne die globale Wahl zu ändern.
  const { einsaetze: ewListe, einsatzIdStr: globalIdStr, setEinsatz: setGlobalEinsatz, fehler: ewFehler, reload: ewReload } = useEinsatz()
  const einsaetze = useMemo<Einsatz[]>(() => ewListe.map(e => ({
    id: e.id, titel: e.titel, status: e.status ?? '', typ: e.typ, einsatzort_adresse: e.raw?.einsatzort_adresse ?? e.adresse ?? null,
  })), [ewListe])
  const [einsatzLokal, setEinsatzLokal] = useState<string | null>(null)
  const einsatzIdForm = einsatzLokal ?? globalIdStr
  const einsatzId = einsatzIdForm
  const [autoEinsatz, setAutoEinsatz] = useState<Einsatz | null>(null)
  const [gesperrt, setGesperrt] = useState(false)
  const [kopfOffen, setKopfOffen] = useState(false)
  const [startOffen, setStartOffen] = useState(false)   // bei laufendem Flug: Startformular eingeklappt   // Pilot + Einsatz einklappbar
  const [pilotGesperrt, setPilotGesperrt] = useState(true)
  const [pilotList, setPilotList] = useState<{id: number; label: string}[]>([])
  const [pilotId, setPilotId] = useState<string>('')
  const [drohneIdForm, setDrohneId] = useState<string>('')
  const drohneId = drohneIdForm
  const [akkuIdForm, setAkkuId] = useState<string>('')
  const akkuId = akkuIdForm
  // Flugart nach Sichtverhältnis (VLOS/EVLOS/BVLOS); Standard bei jedem neuen Flug: VLOS
  const [flugartForm, setFlugart] = useState<string>(STANDARD_FLUGART)
  const flugart = flugartForm

  // Checklisten State
  const [checklisten, setChecklisten] = useState<ChecklistenInstanz[]>([])
  const [checklistenStatus, setChecklistenStatus] = useState<ChecklistenStatus | null>(null)
  const [checklistenLoading, setChecklistenLoading] = useState(false)
  const [checklistenFehler, setChecklistenFehler] = useState<string | null>(null)
  // Nachflug flow state
  const [showNachflugModal, setShowNachflugModal] = useState(false)
  const [nachflugInstanz, setNachflugInstanz] = useState<ChecklistenInstanz | null>(null)
  // Abschluss-Dialog nach "Flug beenden": Nachflug-Checkliste (falls vorhanden) und Vorkommnisse
  const [abschlussFlugId, setAbschlussFlugId] = useState<number | null>(null)
  const [vorkommnis, setVorkommnis] = useState<VorkommnisWert>('NEIN')
  const [vorkommnisBemerkung, setVorkommnisBemerkung] = useState('')
  const [abschlussFehler, setAbschlussFehler] = useState<string | null>(null)
  const [abschlussSpeichert, setAbschlussSpeichert] = useState(false)
  // Meldungen der Drohne (DJI HMS) im Flug: ab „Achtung“ wird ein Vorkommnis mit Sachverhalt vorgeschlagen
  const abschlussHms = useFlugHms(abschlussFlugId && abschlussFlugId > 0 ? abschlussFlugId : null)
  useEffect(() => {
    if (abschlussHms?.vorschlag) {
      setVorkommnis('JA')
      setVorkommnisBemerkung(b => b.trim() ? b : abschlussHms.vorschlag!)
    }
  }, [abschlussHms])
  const [checklistenPanelOpen, setChecklistenPanelOpen] = useState<PilotPhaseKey | null>(null)
  const [currentChecklistenInstanz, setCurrentChecklistenInstanz] = useState<ChecklistenInstanz | null>(null)
  const [updatingItems, setUpdatingItems] = useState<Record<number, boolean>>({})
  
  // VORFLUG Full-Page Modal State
  const [vorflugModalOpen, setVorflugModalOpen] = useState(false)
  const [vorflugItems, setVorflugItems] = useState<ChecklistenItem[]>([])
  // Start über die Aufforderung "Drohne eingeschaltet" (Cloud API): feste Drohne/Einsatz statt der Formularauswahl
  const [vorflugCtx, setVorflugCtx] = useState<StartKontext | null>(null)
  const location = useLocation()
  const navigate = useNavigate()

  const pilotLabel = `${user?.vorname ?? ''} ${user?.nachname ?? ''}`.trim() || user?.benutzername || ''
  
  // Get pilotMode from localStorage (same as Layout)
  const [pilotMode, setPilotMode] = useState<'PILOT' | 'CO_PILOT'>(() => {
    const saved = localStorage.getItem('pilot-hub-pilot-mode')
    return (saved as 'PILOT' | 'CO_PILOT') || 'PILOT'
  })

  // Sync pilotMode from localStorage (when toggled in Layout)
  useEffect(() => {
    const saved = localStorage.getItem("pilot-hub-pilot-mode")
    if (saved && (saved === "PILOT" || saved === "CO_PILOT")) {
      setPilotMode(saved)
    }
    // Listen for storage changes from Layout toggle
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "pilot-hub-pilot-mode" && e.newValue) {
        setPilotMode(e.newValue as "PILOT" | "CO_PILOT")
      }
    }
    window.addEventListener("storage", handleStorageChange)
    return () => window.removeEventListener("storage", handleStorageChange)
  }, [])

  // Akkus der ausgewählten Drohne — nur Bezeichnungen
  const akkusDerDrohne = useMemo<Akku[]>(() => {
    if (!drohneId) return []
    const d = drohnen.find(x => String(x.id) === drohneId)
    return d?.akkus ?? []
  }, [drohnen, drohneId])

  // Laufende Flüge: Serverstand ohne offline bereits beendete + lokal gestartete (noch nicht gesendete)
  const anzeigeFluege = useMemo<AktiverFlug[]>(() => {
    if (!user?.id) return aktiveFluege
    const beendet = lokalBeendeteIds(user.id)
    const lokal: AktiverFlug[] = lokaleFluege(user.id).filter(l => !l.beendet).map(l => ({
      id: -1, lokalUuid: l.uuid, flug_nr: 'Lokal gespeichert', startzeit: l.startzeit, endzeit: null,
      drohne_label: l.anzeige.drohne_label ?? null, akku_label: l.anzeige.akku_label ?? null,
      einsatz_titel: l.anzeige.einsatz_titel ?? null, einsatz_id: l.anzeige.einsatz_id ?? null,
    }))
    return [...lokal, ...aktiveFluege.filter(f => !beendet.has(f.id))]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aktiveFluege, queue, user?.id])

  const laden = useCallback(async () => {
    setLoading(true); setFehler(null)
    try {
      const [f, d] = await Promise.all([
        flightApi.aktive(),
        droneApi.getDropdownList(),
      ])
      setAktiveFluege((f.data as AktiverFlug[]) ?? [])
      setDrohnen((d.data as Drohne[]) ?? [])
      try { localStorage.setItem(DROHNEN_CACHE, JSON.stringify(d.data ?? [])) } catch { /* egal */ }

      if (user?.id) {
        try {
          const z = await missionApi.getMeinKraefteEinsatz(user.id)
          const zu = (z.data as { einsatz: Einsatz | null })?.einsatz ?? null
          setAutoEinsatz(zu)
        } catch {
          // Kräfte-Management-Zuordnung nicht abrufbar: nicht als "keine Zuordnung" ausgeben
          setAutoEinsatz(null)
          setFehler('Die Einsatz-Zuordnung aus dem Kräfte Management konnte nicht geladen werden – bitte Einsatz prüfen.')
        }
      }
    } catch (err: any) {
      if (istVerbindungsfehler(err)) {
        // ohne Verbindung: zuletzt geladene Drohnenliste, damit ein Start offline möglich bleibt
        let gemerkt: Drohne[] = []
        try { gemerkt = JSON.parse(localStorage.getItem(DROHNEN_CACHE) || '[]') } catch { /* egal */ }
        if (gemerkt.length) setDrohnen(gemerkt)
        setFehler('Keine Verbindung – ' + (gemerkt.length ? 'zuletzt geladene Drohnenliste wird verwendet. Starten und Beenden werden gespeichert und später gesendet.' : 'Drohnenliste nicht verfügbar.'))
      } else {
        setFehler(err?.response?.data?.detail || 'Daten konnten nicht geladen werden')
      }
    } finally {
      setLoading(false)
    }
  }, [user?.id])

  useEffect(() => { laden() }, [laden])
  // Nach erfolgreichem Nachsenden aus der Warteschlange: Serverstand neu laden
  useEffect(() => beiGesendet(() => { void laden() }), [laden])
  useConnectionRetry(() => { laden(); void ewReload() })

  // Schloss: solange die globale Wahl der Einsatz aus dem Kräfte Management ist, bleibt die Auswahl gesperrt.
  // Wählt der Nutzer global einen anderen Einsatz, gilt dieser (Schloss offen); ein Zurückwählen sperrt wieder.
  useEffect(() => {
    setEinsatzLokal(null)
    setGesperrt(!!autoEinsatz && globalIdStr === String(autoEinsatz.id))
  }, [globalIdStr, autoEinsatz?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  // Piloten laden (für Pilot-Auswahl)
  useEffect(() => {
    const fetchPilots = async () => {
      try {
        const res = await userApi.list({ funktion: 'PILOT', size: 500 })
        const pilots = ((res.data?.items as any[]) || []).map(u => ({
          id: u.id,
          // ohne Vor-/Nachname den Benutzernamen zeigen
          label: `${u.vorname || ''} ${u.nachname || ''}`.trim() || u.benutzername
        }))
        setPilotList(pilots)
        // Standard: eingeloggter Benutzer vorauswählen
        if (user?.id && !pilotId) {
          setPilotId(user.id.toString())
        }
      } catch {
        // Fehler (Netzwerk/5xx) meldet der Interceptor; zusätzlich sichtbar, damit die Pilotenauswahl nicht leer wirkt
        setFehler('Die Pilotenliste konnte nicht geladen werden – es wird der angemeldete Benutzer verwendet.')
        if (user?.id && !pilotId) setPilotId(user.id.toString())
      }
    }
    fetchPilots()
  }, [user?.id])



  // Checklisten laden wenn einsatzId sich ändert
  const ladenChecklisten = useCallback(async () => {
    if (!einsatzId) {
      setChecklisten([])
      setChecklistenStatus(null)
      return
    }
    setChecklistenLoading(true)
    setChecklistenFehler(null)
    const mein = ++ladeSeq.current
    try {
      const [clRes, statusRes] = await Promise.all([
        checklistenApi.getEinsatzChecklisten(Number(einsatzId)),
        checklistenApi.getFluegeChecklistenStatus(Number(einsatzId)),
      ])
      // veraltete Antwort (inzwischen neuer geladen) oder laufendes Speichern: verwerfen
      if (mein !== ladeSeq.current || speicherAnz.current > 0) return
      // Filter: Nur PILOT-Checklisten
      // Wenn ein Flug aktiv ist, nur Checklisten für diesen Flug + Einsatzende_Pilot (flug_id = null)
      const allChecklisten = ((clRes.data as ChecklistenInstanz[]) || []).filter(c => c.modul === 'PILOT')
      const activeFlug = aktiveFluege.find(f => f.id === Number(einsatzId)) // This won't work, einsatzId != flugId
      // Instead, we'll filter in the render logic based on the active flight
      setChecklisten(allChecklisten)
      setChecklistenStatus(statusRes.data as ChecklistenStatus)
    } catch (err: any) {
      setChecklistenFehler(err?.response?.data?.detail || 'Checklisten konnten nicht geladen werden')
    } finally {
      setChecklistenLoading(false)
    }
  }, [einsatzId, aktiveFluege])

  useEffect(() => { ladenChecklisten() }, [ladenChecklisten])

  // Drohne wechseln → Akku zurücksetzen
  const waehleDrohne = (val: string) => {
    setDrohneId(val)
    setAkkuId('')
    setVorbelegt(v => ({ ...v, drohne: false, akku: false }))
  }
  const waehleAkku = (val: string) => { setAkkuId(val); setVorbelegt(v => ({ ...v, akku: false })) }
  const waehleFlugart = (val: string) => { setFlugart(val); setVorbelegt(v => ({ ...v, flugart: false })) }

  // Vorbelegung: zuletzt verwendete Drohne, Flugart und (falls noch frei) Akku dieses Benutzers
  useEffect(() => {
    if (!user?.id || drohnen.length === 0 || drohneId) return
    const z = zuletztLesen(user.id)
    if (!z) return
    const d = drohnen.find(x => String(x.id) === z.drohneId)
    const neu = { drohne: false, akku: false, flugart: false }
    if (d) {
      setDrohneId(String(d.id)); neu.drohne = true
      // Akku nur, wenn er zur Drohne gehört und nicht in einem laufenden bzw. noch gespeicherten Flug steckt
      const belegt = new Set<number>([
        ...aktiveFluege.map(f => f.akku_id).filter((x): x is number => typeof x === 'number'),
        ...queue.filter(e => e.typ === 'start').map(e => Number(e.payload.akku_id)).filter(Number.isFinite),
      ])
      const a = (d.akkus ?? []).find(x => String(x.id) === z.akkuId)
      if (a && !belegt.has(a.id) && !akkuId) { setAkkuId(String(a.id)); neu.akku = true }
    }
    setVorbelegt(neu)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, drohnen, vorbelegenTick])

  // Akku-Optionen: zugeordnete Akkus der Drohne + immer "Fremd-Akku" als unterster Punkt
  const akkuOptionen = useMemo(
    () => [
      ...akkusDerDrohne.map(a => ({ value: String(a.id), label: akkuAnzeige(a) })),
      { value: FREMDAKKU, label: 'Fremd-Akku' },
    ],
    [akkusDerDrohne]
  )

  // Drohnen, die dem gewählten Einsatz zugeordnet sind (aktiv gebunden): in der Auswahl oben, gekennzeichnet
  const [einsatzDrohnenIds, setEinsatzDrohnenIds] = useState<number[]>([])
  useEffect(() => {
    if (!einsatzId) { setEinsatzDrohnenIds([]); return }
    let abgebrochen = false
    api.get(`/einsaetze/${einsatzId}/drohnen`)
      .then(r => { if (!abgebrochen) setEinsatzDrohnenIds(((r.data as { drohne_id: number; freigegeben?: boolean }[]) ?? []).filter(x => !x.freigegeben).map(x => x.drohne_id)) })
      .catch(() => { if (!abgebrochen) setEinsatzDrohnenIds([]) })
    return () => { abgebrochen = true }
  }, [einsatzId])
  const drohnenOptionen = useMemo(() => {
    const z = new Set(einsatzDrohnenIds)
    const opt = (x: Drohne) => ({ value: String(x.id), label: z.has(x.id) ? `★ ${x.label}` : x.label })
    return [...drohnen.filter(x => z.has(x.id)).map(opt), ...drohnen.filter(x => !z.has(x.id)).map(opt)]
  }, [drohnen, einsatzDrohnenIds])

  // Checklisten Panel Functions
  const openChecklistenPanel = (phase: PilotPhaseKey) => {
    const instanz = checklisten.find(c => c.phase === phase)
    if (instanz) {
      setCurrentChecklistenInstanz(instanz)
      setChecklistenPanelOpen(phase)
    }
  }

  const closeChecklistenPanel = () => {
    setChecklistenPanelOpen(null)
    setCurrentChecklistenInstanz(null)
  }

  // Speichern: jeder Haken sofort sichtbar (optimistisch), Speicheraufrufe laufen nacheinander, und das Neuladen
  // verwirft veraltete Antworten bzw. wartet, bis alles gespeichert ist (sonst überschreibt eine späte Antwort
  // den zweiten schnell gesetzten Haken und er wirkt "nicht gespeichert").
  const speicherAnz = useRef(0)
  const ladeSeq = useRef(0)
  const speicherKette = useRef<Promise<unknown>>(Promise.resolve())

  // Stand der geöffneten Panels/Dialoge aus einer Instanzliste nachziehen
  const offeneInstanzenAktualisieren = (alle: ChecklistenInstanz[]) => {
    setCurrentChecklistenInstanz(cur => (cur && alle.find(c => c.id === cur.id)) || cur)
    setNachflugInstanz(cur => (cur && alle.find(c => c.id === cur.id)) || cur)
  }

  async function neuLadenChecklisten() {
    const mein = ++ladeSeq.current
    const clRes = await checklistenApi.getEinsatzChecklisten(Number(einsatzId))
    const statusRes = await checklistenApi.getFluegeChecklistenStatus(Number(einsatzId))
    if (mein !== ladeSeq.current || speicherAnz.current > 0) return
    const alle = (clRes.data as ChecklistenInstanz[]) || []
    setChecklisten(alle.filter(c => c.modul === 'PILOT'))
    setChecklistenStatus(statusRes.data as ChecklistenStatus)
    offeneInstanzenAktualisieren(alle)
  }

  async function speichernItems(aenderungen: { instanzId: number; itemId: number; wert: string }[], fehlerText = 'Speichern fehlgeschlagen') {
    const mitWerten = (c: ChecklistenInstanz): ChecklistenInstanz => {
      const mine = aenderungen.filter(x => x.instanzId === c.id)
      return mine.length ? { ...c, items: c.items.map(i => { const x = mine.find(m => m.itemId === i.id); return x ? { ...i, wert: x.wert } : i }) } : c
    }
    setFehler(null)
    setChecklisten(prev => prev.map(mitWerten))
    setCurrentChecklistenInstanz(cur => cur && mitWerten(cur))
    setNachflugInstanz(cur => cur && mitWerten(cur))
    setUpdatingItems(prev => { const n = { ...prev }; aenderungen.forEach(x => { n[x.itemId] = true }); return n })
    speicherAnz.current++
    const lauf = speicherKette.current.then(async () => {
      for (const x of aenderungen) await checklistenApi.updateInstanzItem(x.instanzId, x.itemId, { wert: x.wert })
    })
    speicherKette.current = lauf.catch(() => {})
    try {
      await lauf
    } catch (err: any) {
      setFehler(err?.response?.data?.detail || fehlerText)
    } finally {
      speicherAnz.current--
      setUpdatingItems(prev => { const n = { ...prev }; aenderungen.forEach(x => { n[x.itemId] = false }); return n })
    }
    if (speicherAnz.current === 0) { try { await neuLadenChecklisten() } catch { /* nächster Versuch */ } }
  }

  const updateChecklistenItem = (instanzId: number, itemId: number, wert: string) =>
    speichernItems([{ instanzId, itemId, wert }])

  async function completeChecklistenInstanz(instanzId: number) {
    try {
      await checklistenApi.updateInstanz(instanzId, { status: 'ABGESCHLOSSEN' })
      const clRes = await checklistenApi.getEinsatzChecklisten(Number(einsatzId))
      setChecklisten(((clRes.data as ChecklistenInstanz[]) || []).filter(c => c.modul === 'PILOT'))
      const statusRes = await checklistenApi.getFluegeChecklistenStatus(Number(einsatzId))
      setChecklistenStatus(statusRes.data as ChecklistenStatus)
    } catch (err: any) {
      setFehler(err?.response?.data?.detail || 'Status ändern fehlgeschlagen')
    }
  }

  async function starteFlug(ctx?: StartKontext) {
    const drohneId = ctx ? ctx.drohneId : drohneIdForm
    const einsatzId = ctx ? ctx.einsatzId : einsatzIdForm
    if (!user?.id || !drohneId) return
    // Weicher Hinweis (keine Sperre): Einsatzbereitschaft / am Einsatzort noch offen. Funktioniert mit dem zuletzt geladenen
    // Stand, daher auch offline; der Start selbst (flugQueue) bleibt unverändert.
    if (einsatzId) {
      const gewaehlt = ewListe.find(e => String(e.id) === einsatzId)
      const offen = ['EINSATZORT', 'EINSATZBEREITSCHAFT']
        .map(ph => checklisten.find(c => c.phase === ph && !c.flug_id))
        .filter((c): c is ChecklistenInstanz => !!c && !checklistenFortschritt(c).fertig)
      if (offen.length) {
        const ok = await bestaetigen({
          titel: 'Checkliste noch offen',
          text: `Noch offen: ${offen.map(c => `${checklistenAnzeigeName(c.phase, gewaehlt?.typ) ?? c.name} (${checklistenFortschritt(c).offen} Punkte)`).join(', ')}.`,
          hinweis: 'Der Co-Pilot hakt sie ab. Du kannst den Flug trotzdem starten.',
          bestaetigenText: 'Trotzdem starten', gefahr: false,
        })
        if (!ok) return
      }
    }
    // Vorflug-Checkliste aus der Vorlage des Einsatzes VOR dem Start ausfüllen; die Werte gehen beim Anlegen
    // in die eigene Vorflug-Checkliste des neuen Flugs (nicht in die eines früheren Flugs)
    if (einsatzId) {
      try {
        type VorlageDaten = { items: Array<{ vorlage_item_id: number; text: string; typ: ChecklistenItem['typ']; pflicht: boolean; sortierung: number; hinweis: string | null }> }
        let daten: VorlageDaten
        try {
          const r = await checklistenApi.getVorflugVorlage(Number(einsatzId))
          daten = r.data as VorlageDaten
          try { localStorage.setItem(vorlageKey(einsatzId), JSON.stringify(daten)) } catch { /* egal */ }
        } catch (e: any) {
          if (!istVerbindungsfehler(e)) throw e
          // ohne Verbindung: zuletzt geladene Vorlage dieses Einsatzes verwenden
          let gemerkt: VorlageDaten | null = null
          try { gemerkt = JSON.parse(localStorage.getItem(vorlageKey(einsatzId)) || 'null') } catch { /* egal */ }
          if (!gemerkt) {
            setFehler('Keine Verbindung und keine gespeicherte Vorflug-Checkliste für diesen Einsatz – Flug nicht gestartet. Bitte Verbindung prüfen.')
            return
          }
          daten = gemerkt
        }
        if (daten.items.length) {
          setVorflugItems(daten.items.map(i => ({
            id: i.vorlage_item_id, instanz_id: 0, vorlage_item_id: i.vorlage_item_id, text: i.text, typ: i.typ, pflicht: i.pflicht,
            sortierung: i.sortierung, hinweis: i.hinweis, wert: null, ausgefuellt_am: null, ausgefuellt_von: null,
          })))
          setVorflugCtx(ctx ?? null)
          setVorflugModalOpen(true)
          return
        }
      } catch (err: any) {
        // 404 = keine Vorlage vorhanden (ok). Anderer Fehler: sichtbar machen, Start bleibt möglich.
        if (err?.response?.status !== 404) {
          setFehler('Die Vorflug-Checkliste konnte nicht geladen werden – Flug nicht gestartet. Bitte erneut versuchen.')
          return
        }
      }
    }
    await startFlightAfterChecklist({}, ctx)
  }

  // VORFLUG Modal Handlers
  const fillAllVorflugPflicht = () => {
    setVorflugItems(prev => prev.map(item => 
      item.pflicht && item.typ === 'CHECK' && !item.wert ? { ...item, wert: 'true' } : item
    ))
  }

  const confirmVorflug = async () => {
    const pflichtOffen = vorflugItems.filter(i => i.pflicht && (!i.wert || i.wert === '')).length
    if (pflichtOffen > 0) {
      setFehler(`Vorflug-Checkliste hat noch ${pflichtOffen} offene Pflichtfelder.`)
      return
    }
    const werte = Object.fromEntries(vorflugItems.filter(i => i.wert).map(i => [String(i.vorlage_item_id), i.wert as string]))
    setVorflugModalOpen(false)
    setVorflugItems([])
    setFehler(null)
    await startFlightAfterChecklist(werte, vorflugCtx ?? undefined)
  }

  const cancelVorflug = () => {
    // Übernahme zurückgeben, damit ein anderer Pilot die Vorflugkontrolle machen kann
    if (vorflugCtx) void api.post(`/pilot/vorflug/${vorflugCtx.drohneId}/freigeben`).catch(() => {})
    setVorflugCtx(null)
    setVorflugModalOpen(false)
    setVorflugItems([])
  }

  /** Flug anlegen; vorflugWerte = ausgefüllte Vorflug-Punkte (vorlage_item_id -> Wert).
   *  Ohne Verbindung wird der Start mit Erfassungszeit lokal gespeichert und später gesendet (UUID verhindert Doppelflüge). */
  const startFlightAfterChecklist = async (vorflugWerte: Record<string, string>, ctx?: StartKontext) => {
    const drohneId = ctx ? ctx.drohneId : drohneIdForm
    const einsatzId = ctx ? ctx.einsatzId : einsatzIdForm
    const akkuId = ctx ? '' : akkuIdForm
    const flugart = ctx ? STANDARD_FLUGART : flugartForm
    if (!user?.id || !drohneId) return
    setStartet(true); setFehler(null); setGpsHinweis(null); setOfflineHinweis(null)
    const erfasst = lokaleZeit()
    const uuid = neueUuid()
    try {
      const gewaehlterEinsatz = einsaetze.find(e => String(e.id) === einsatzId)
      const fremdAkku = akkuId === FREMDAKKU
      const pos = await holePosition()
      if (pos) setGpsHinweis(`Startpunkt per GPS übernommen: ${coordsText(pos)}`)
      else setGpsHinweis('Kein GPS verfügbar — Startpunkt ohne Koordinaten gespeichert.')
      const payload: Record<string, unknown> = {
        // entsperrte Auswahl berücksichtigen, sonst der angemeldete Benutzer
        pilot_id: Number(pilotId) || user.id,
        drohne_id: Number(drohneId),
        flugart: flugart || null,
        akku_id: akkuId && !fremdAkku ? Number(akkuId) : null,
        einsatz_id: einsatzId ? Number(einsatzId) : null,
        startort: pos ? coordsText(pos) : (gewaehlterEinsatz?.einsatzort_adresse || 'Unbekannt'),
        startort_lat: pos ? String(pos.lat) : null,
        startort_lon: pos ? String(pos.lon) : null,
      }
      if (fremdAkku) payload.bemerkungen = 'Fremd-Akku'
      if (Object.keys(vorflugWerte).length) payload.vorflug_werte = vorflugWerte
      let offline = false
      try {
        await api.post('/fluege', { ...payload, client_uuid: uuid }, { timeout: TIMEOUT_MS })
      } catch (err: any) {
        if (!istVerbindungsfehler(err)) throw err
        // Startzeit = Zeitpunkt der Erfassung, nicht des Sendens
        const anzeige: StartAnzeige = {
          drohne_label: drohnen.find(d => String(d.id) === drohneId)?.label ?? null,
          akku_label: fremdAkku ? 'Fremd-Akku' : (akkusDerDrohne.find(a => String(a.id) === akkuId)?.bezeichnung ?? null),
          einsatz_titel: gewaehlterEinsatz?.titel ?? null,
          einsatz_id: gewaehlterEinsatz?.id ?? null,
        }
        queueHinzufuegen({ typ: 'start', qid: neueQid(), nutzerId: user.id, erfasstAm: erfasst, uuid, payload: { ...payload, startzeit: erfasst }, anzeige })
        offline = true
        setOfflineHinweis(OFFLINE_HINWEIS)
      }
      // Auswahl merken (pro Benutzer) und für den nächsten Flug neu vorbelegen
      if (!ctx) {
        zuletztSchreiben(user.id, { drohneId, akkuId: fremdAkku ? '' : akkuId, flugart })
        setDrohneId(''); setAkkuId(''); setFlugart(STANDARD_FLUGART)
        setVorbelegt({ drohne: false, akku: false, flugart: false })
      } else {
        setVorflugCtx(null)
        aufforderungenNeuLaden()
      }
      if (!offline) await laden()
      setVorbelegenTick(t => t + 1)
    } catch (err: any) {
      setFehler(err?.response?.data?.detail || 'Flug konnte nicht gestartet werden')
    } finally {
      setStartet(false)
    }
  }

  async function beendeFlug(id: number, lokalUuid?: string) {
    // Check LANDUNG checklist for THIS specific flight (must be completed before ending)
    const landungInstanz = lokalUuid ? undefined : checklisten.find(c => c.phase === 'LANDUNG' && c.flug_id === id)
    
    const blockierendePhasen: string[] = []
    if (landungInstanz) {
      const offen = landungInstanz.items.filter(i => i.pflicht && (!i.wert || i.wert === '')).length
      if (offen > 0) blockierendePhasen.push(`Landung (${offen} offen)`)
    }
    
    if (blockierendePhasen.length > 0) {
      setFehler(`Flug kann nicht beendet werden. Offene Checklisten: ${blockierendePhasen.join(', ')}. Bitte zuerst alle Pflichtfelder ausfüllen.`)
      return
    }
    
    setBeendetId(id); setFehler(null); setGpsHinweis(null); setOfflineHinweis(null)
    const erfasst = lokaleZeit()
    try {
      const pos = await holePosition()
      if (pos) setGpsHinweis(`Zielort per GPS übernommen: ${coordsText(pos)}`)
      else setGpsHinweis('Kein GPS verfügbar — Zielort ohne Koordinaten gespeichert.')
      const ziel = pos ? { zielort_lat: pos.lat, zielort_lon: pos.lon, zielort: coordsText(pos) } : undefined
      const flugEinsatz = anzeigeFluege.find(f => lokalUuid ? f.lokalUuid === lokalUuid : f.id === id)?.einsatz_id

      // 1. Flug beenden; ohne Verbindung mit Erfassungszeit lokal speichern (Ende nach Start, Reihenfolge der Warteschlange)
      let offline = !!lokalUuid
      if (!lokalUuid) {
        try {
          await api.put(`/fluege/${id}/ende`, ziel ?? null, { timeout: TIMEOUT_MS })
        } catch (err: any) {
          if (!istVerbindungsfehler(err)) throw err
          offline = true
        }
      }
      if (offline && user?.id) {
        const ref: FlugRef = lokalUuid ? { uuid: lokalUuid } : { id }
        queueHinzufuegen({ typ: 'ende', qid: neueQid(), nutzerId: user.id, erfasstAm: erfasst, ref, daten: { ...(ziel ?? {}), endzeit: erfasst } })
        setOfflineHinweis(OFFLINE_HINWEIS)
        void queueSenden(user.id)
      }

      // 2. Nachflug-Checkliste dieses Flugs frisch laden und öffnen (ohne Verbindung nicht möglich)
      let nachflug: ChecklistenInstanz | undefined
      if (!offline) {
        await laden()
        const frisch = flugEinsatz ? (((await checklistenApi.getEinsatzChecklisten(Number(flugEinsatz))).data as ChecklistenInstanz[]) || []) : []
        nachflug = frisch.find(c => c.phase === 'NACHFLUG' && c.flug_id === id)
      }

      // Abschluss-Dialog bei jedem Flug: Vorkommnisse angeben, ggf. mit Nachflug-Checkliste
      setNachflugInstanz(nachflug ?? null)
      setAbschlussFlugId(id)
      setAbschlussUuid(lokalUuid ?? null)
      setVorkommnis('NEIN'); setVorkommnisBemerkung(''); setAbschlussFehler(null)
      setShowNachflugModal(true)
    } catch (err: any) {
      setFehler(err?.response?.data?.detail || 'Flug konnte nicht beendet werden')
    } finally {
      setBeendetId(null)
    }
  }

  // Nachflug handlers
  const updateNachflugItem = async (itemId: number, wert: string) => {
    if (!nachflugInstanz) return
    await speichernItems([{ instanzId: nachflugInstanz.id, itemId, wert }])
  }

  const markAllNachflugPflicht = async () => {
    if (!nachflugInstanz) return
    await speichernItems(
      nachflugInstanz.items.filter(i => i.typ === 'CHECK' && i.pflicht && (!i.wert || i.wert === '')).map(i => ({ instanzId: nachflugInstanz.id, itemId: i.id, wert: 'true' })),
      'Markieren fehlgeschlagen')
  }

  const cancelNachflug = () => {
    setAbschlussUuid(null)
    setShowNachflugModal(false)
    setNachflugInstanz(null)
    setAbschlussFlugId(null)
  }

  const confirmNachflug = async () => {
    if (!abschlussFlugId) return

    // Check all Pflicht fields filled
    const pflichtOffen = nachflugInstanz ? nachflugInstanz.items.filter(i => i.pflicht && (!i.wert || i.wert === '')).length : 0
    if (pflichtOffen > 0) {
      setAbschlussFehler(`Nachflug-Checkliste hat noch ${pflichtOffen} offene Pflichtfelder.`)
      return
    }
    const vkFehler = vorkommnisFehler(vorkommnis, vorkommnisBemerkung, true)
    if (vkFehler) { setAbschlussFehler(vkFehler); return }

    setAbschlussSpeichert(true); setAbschlussFehler(null)
    try {
      const daten = {
        vorkommnis: vorkommnisZuApi(vorkommnis),
        vorkommnis_bemerkung: vorkommnisBemerkungZuApi(vorkommnis, vorkommnisBemerkung),
      }
      const inQueueSpeichern = (ref: FlugRef) => {
        if (!user?.id) return
        queueHinzufuegen({ typ: 'update', qid: neueQid(), nutzerId: user.id, erfasstAm: lokaleZeit(), ref, daten })
        setOfflineHinweis(OFFLINE_HINWEIS)
        void queueSenden(user.id)
      }
      if (abschlussUuid) {
        // Start liegt noch in der Warteschlange: Angaben hinten anstellen (laufen nach Start und Ende)
        inQueueSpeichern({ uuid: abschlussUuid })
        cancelNachflug()
        return
      }
      try {
        await api.put(`/fluege/${abschlussFlugId}`, daten, { timeout: TIMEOUT_MS })
      } catch (err: any) {
        if (!istVerbindungsfehler(err)) throw err
        inQueueSpeichern({ id: abschlussFlugId })
        cancelNachflug()
        return
      }
      // Mark Nachflug instanz as completed
      await speicherKette.current // erst alle Haken speichern
      if (nachflugInstanz) await checklistenApi.updateInstanz(nachflugInstanz.id, { status: 'ABGESCHLOSSEN' })

      await laden()
      aufforderungenNeuLaden()
      cancelNachflug()
    } catch (err: any) {
      setAbschlussFehler(err?.response?.data?.detail || 'Flug beenden fehlgeschlagen')
    } finally {
      setAbschlussSpeichert(false)
    }
  }

  // Aufträge aus den Bannern (CloudAufforderungen): Vorflugkontrolle für die eingeschaltete Drohne, Nachflug nach erkannter Landung
  const auftragRef = useRef<(a: any) => Promise<void>>(async () => {})
  auftragRef.current = async (a: any) => {
    if (a.typ === 'vorflug') {
      // Übernehmen: wer zuerst tippt, macht die Vorflugkontrolle. Ohne Verbindung trotzdem starten (Offline-Warteschlange).
      try {
        await api.post(`/pilot/vorflug/${a.drohneId}/uebernehmen`)
      } catch (err: any) {
        if (!istVerbindungsfehler(err)) {
          setFehler(err?.response?.data?.detail || 'Vorflugkontrolle konnte nicht übernommen werden')
          aufforderungenNeuLaden()
          return
        }
      }
      const ctx = { drohneId: String(a.drohneId), einsatzId: String(a.einsatzId) }
      // Vorflug schon im Pop-up ausgefüllt: vorbereiteten Flug direkt anlegen (Zusammenführung beim Abheben wie bei "Flug starten")
      if (a.werte && typeof a.werte === 'object') await startFlightAfterChecklist(a.werte as Record<string, string>, ctx)
      else await starteFlug(ctx)
    } else if (a.typ === 'nachflug') {
      if (abschlussFlugId) return
      try {
        const frisch = ((await checklistenApi.getEinsatzChecklisten(a.einsatzId)).data as ChecklistenInstanz[]) || []
        setNachflugInstanz(frisch.find(c => c.phase === 'NACHFLUG' && c.flug_id === a.flugId) ?? null)
        setAbschlussFlugId(a.flugId); setAbschlussUuid(null)
        setVorkommnis('NEIN'); setVorkommnisBemerkung(''); setAbschlussFehler(null)
        setShowNachflugModal(true)
      } catch (err: any) {
        setFehler(err?.response?.data?.detail || 'Nachflug-Checkliste konnte nicht geladen werden')
      }
    }
  }
  const auftragState = (location.state as { auftrag?: any } | null)?.auftrag
  useEffect(() => {
    if (!auftragState) return
    navigate(location.pathname, { replace: true, state: null })
    void auftragRef.current(auftragState)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key])

  // Pilot Phasen sortiert

  const sortedPhases = useMemo(() => [...PILOT_PHASES].sort((a, b) => a.order - b.order), [])

  // Checklisten für jede Phase - filter by flug_id when flight is active
  const getInstanzForPhase = (phase: PilotPhaseKey, flugId?: number) => {
    if (flugId) {
      return checklisten.find(c => c.phase === phase && c.flug_id === flugId)
    }
    return checklisten.find(c => c.phase === phase)
  }
  const getPflichtCount = (instanz: ChecklistenInstanz | undefined) => 
    instanz?.items.filter(i => i.pflicht).length ?? 0
  const getErledigtCount = (instanz: ChecklistenInstanz | undefined) => 
    instanz?.items.filter(i => i.pflicht && i.wert && i.wert !== '').length ?? 0
  const isComplete = (instanz: ChecklistenInstanz | undefined) => {
    const pflicht = getPflichtCount(instanz)
    return pflicht > 0 && getErledigtCount(instanz) === pflicht
  }
  const isInProgress = (instanz: ChecklistenInstanz | undefined) => {
    const pflicht = getPflichtCount(instanz)
    return pflicht > 0 && getErledigtCount(instanz) > 0 && getErledigtCount(instanz) < getPflichtCount(instanz)
  }

  // Determine current active phase based on flight status
  const activeFlug = aktiveFluege[0]
  const hasActiveFlug = aktiveFluege.length > 0
  const flugStatus = activeFlug ? 'active' : 'none'

  const jetztTakt = useSekundenTakt(anzeigeFluege.length > 0)
  const formularZu = anzeigeFluege.length > 0 && !startOffen
  useEffect(() => { setStartOffen(false) }, [anzeigeFluege.length])
  const pilotLabelShort = `${user?.vorname?.[0] || ''}${user?.nachname?.[0] || ''}` || user?.benutzername?.[0] || '?'

  return (
    <div className="space-y-6">
      {bestaetigungsDialog}
      {alleOkDialog}
      <PageHeader
        title=""
        subtitle="Flüge starten und laufende Flüge beenden"
      />

      {fehler && (
        <div className="flex items-center gap-2 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-200">
          <AlertCircle className="w-4 h-4 shrink-0" />
          <span>{fehler}</span>
        </div>
      )}

      {/* ===== Laufender Flug zuerst: große Flugkarte (Glass, Leuchtkante) ===== */}
      {loading ? (
        <Card className="p-4 sm:p-5"><div className="py-4 text-center text-sm text-dark-text-muted">Lade Flüge…</div></Card>
      ) : anzeigeFluege.length === 0 ? (
        <div className="flex items-center gap-2 rounded-xl border border-dark-border bg-dark-bg-tertiary/30 px-4 py-3 text-sm text-dark-text-muted">
          <Plane className="h-4 w-4 shrink-0" /> Zurzeit läuft kein Flug.
        </div>
      ) : (
        <div className="space-y-3">
          {anzeigeFluege.map(f => (
            <div
              key={f.lokalUuid ?? f.id}
              className="rounded-2xl border border-[rgb(125_211_252/0.55)] bg-dark-bg-secondary/70 p-4 shadow-[0_0_28px_rgb(125_211_252/0.22)] backdrop-blur sm:p-5"
              data-testid="laufender-flug"
            >
              <div className="flex flex-wrap items-center gap-2">
                <span className="relative flex h-3 w-3 shrink-0"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400/70" /><span className="relative inline-flex h-3 w-3 rounded-full bg-emerald-400" /></span>
                <span className="text-sm font-semibold uppercase tracking-wide text-emerald-300">Flug läuft</span>
                <span className="font-mono text-sm font-semibold text-dark-text">{f.flug_nr}</span>
                {f.lokalUuid && <Badge variant="warning">Offline gespeichert – noch nicht gesendet</Badge>}
                {f.einsatz_titel && <Badge variant="info">{f.einsatz_titel}</Badge>}
              </div>
              <div className="mt-2 text-[48px] font-bold leading-none tabular-nums text-dark-text" aria-label="Laufzeit">
                {formatLaufzeit(f.startzeit, jetztTakt)}
              </div>
              <div className="mt-3 grid grid-cols-1 gap-x-6 gap-y-1 text-base text-dark-text-muted sm:grid-cols-3">
                <div className="truncate"><span className="text-dark-text-subtle">Drohne:</span> {f.drohne_label || '—'}</div>
                <div className="truncate"><span className="text-dark-text-subtle">Akku:</span> {f.akku_label || '—'}</div>
                <div className="flex items-center gap-1.5 truncate"><Clock className="h-4 w-4 shrink-0" /><span>Start {fmtZeit(f.startzeit)}</span></div>
              </div>
              <button
                type="button"
                onClick={() => beendeFlug(f.id, f.lokalUuid)}
                disabled={beendetId !== null}
                className="mt-4 flex min-h-[72px] w-full items-center justify-center gap-3 rounded-2xl bg-red-600 text-xl font-bold text-white shadow-lg transition-colors hover:bg-red-500 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <Square className="h-6 w-6" />
                {beendetId === f.id ? 'Beende…' : 'Flug beenden'}
              </button>
            </div>
          ))}
        </div>
      )}

      {einsatzId && <MeinTrupp einsatzId={einsatzId} />}

      {einsatzId && <EinsatzChecklistenLesen einsatz={ewListe.find(e => String(e.id) === einsatzId)?.raw} checklisten={checklisten as any} />}

      {gpsHinweis && (
        <div className="flex items-center gap-2 rounded-xl border border-primary-600/30 bg-primary-600/10 px-4 py-3 text-sm text-primary-200">
          <MapPin className="w-4 h-4 shrink-0" />
          <span>{gpsHinweis}</span>
        </div>
      )}

      {(offlineHinweis || meineQueue.length > 0) && (
        <div role="status" className="flex items-center gap-2 rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-200" data-testid="offline-hinweis">
          <CloudOff className="w-4 h-4 shrink-0" />
          <span>
            {offlineHinweis ?? OFFLINE_HINWEIS}
            {meineQueue.length > 0 && <strong className="ml-1">Ausstehend: {meineQueue.length}</strong>}
          </span>
        </div>
      )}

      {/* Nachflug-Dialog (am Handy Vollbild-Sheet); ohne Abbrechen: Abschluss ist Pflicht */}
      <Dialog
        isOpen={!!(showNachflugModal && abschlussFlugId)}
        onClose={() => { /* Abschluss nicht abbrechbar */ }}
        showCloseButton={false}
        title={nachflugInstanz ? 'Nachflug-Checkliste' : 'Flug abschließen'}
        maxWidth="xl"
        footer={<>
          {abschlussFehler && <p className="text-sm text-red-300">{abschlussFehler}</p>}
          {nachflugInstanz && (
            <Button variant="secondary" onClick={async () => { if (await frageAlleOk(nachflugInstanz.items.filter(i => i.typ === 'CHECK' && i.pflicht && !i.wert).length)) await markAllNachflugPflicht() }} className="min-h-[48px]">
              Alle Pflichtpunkte ok
            </Button>
          )}
          <Button variant="primary" onClick={confirmNachflug} disabled={abschlussSpeichert} className="min-h-[48px]">
            {abschlussSpeichert ? 'Speichern…' : 'Bestätigen'}
          </Button>
        </>}
      >
        <p className="mb-4 text-sm text-dark-text-muted">
          {nachflugInstanz ? 'Alle Pflichtfelder und die Vorkommnisse müssen vor dem Flugende ausgefüllt sein' : 'Gab es Vorkommnisse?'}
        </p>
            <div className="space-y-4">
              {nachflugInstanz?.items.map((item: any) => (
                <ChecklistZeile key={item.id} typ={item.typ} className="p-4 rounded-xl border border-dark-border bg-dark-bg-tertiary/50">
                  <div className="flex items-center gap-3">
                    {item.typ === 'CHECK' && (
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          checked={item.wert === 'true'}
                          onChange={(e) => updateNachflugItem(item.id, e.target.checked ? 'true' : '')}
                          className="haken"
                        />
                      </div>
                    )}
                    {item.typ === 'TEXT' && (
                      <ChecklistenEingabe
                        type="text"
                        wert={item.wert || ''}
                        onSpeichern={(v) => updateNachflugItem(item.id, v)}
                        className="flex-1 px-3 py-2 rounded border border-dark-border bg-dark-bg text-dark-text text-base"
                        placeholder="Freitext..."
                      />
                    )}
                    {item.typ === 'NUMBER' && (
                      <ChecklistenEingabe
                        type="number"
                        wert={zahlWert(item.wert)}
                        onSpeichern={(v) => updateNachflugItem(item.id, v)}
                        className="w-32 px-3 py-2 rounded border border-dark-border bg-dark-bg text-dark-text text-base"
                        placeholder="Zahl"
                      />
                    )}
                    {item.typ === 'PHOTO' && (
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => { /* photo upload */ }}
                        className="px-3 py-2 rounded border border-dark-border bg-dark-bg text-dark-text text-sm"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className={`font-medium text-base ${item.pflicht ? 'text-dark-text' : 'text-dark-text-muted'}`}>
                        {item.text}
                        {item.pflicht && <span className="checklist-pflicht">Pflicht</span>}
                      </p>
                      {item.hinweis && <p className="text-sm text-dark-text-muted mt-1">{item.hinweis}</p>}
                    </div>
                  </div>
                </ChecklistZeile>
              ))}

              {/* Vorkommnisse dieses Flugs (Flugbuch / Flugbericht) */}
              <div className="p-4 rounded-xl border border-dark-border bg-dark-bg-tertiary/50">
                <VorkommnisFelder wert={vorkommnis} bemerkung={vorkommnisBemerkung} hms={abschlussHms}
                  onChange={(w, b) => { setVorkommnis(w); setVorkommnisBemerkung(b); setAbschlussFehler(null) }} />
              </div>
            </div>

      </Dialog>

      {/* ===== Neuen Flug starten ===== */}
      {formularZu && (
        <button type="button" onClick={() => setStartOffen(true)}
          className="flex min-h-[64px] w-full items-center justify-center gap-2 rounded-2xl border border-dark-border bg-dark-bg-tertiary/50 text-base font-semibold text-dark-text transition-colors hover:border-primary-500/60">
          <Play className="h-5 w-5 text-primary-400" /> Weiteren Flug starten
        </button>
      )}
      {!formularZu && <Card className="p-4 sm:p-6">
        <div className="mb-5 flex items-center gap-2">
          <Play className="h-5 w-5 text-primary-400" />
          <h2 className="text-base font-semibold text-dark-text">Neuen Flug starten</h2>
        </div>

        <div className="space-y-5">
          {/* Pilot + Einsatz: eine einklappbare Zeile */}
          <div className="rounded-xl border border-dark-border bg-dark-bg-tertiary/40">
            <button
              type="button"
              onClick={() => setKopfOffen(o => !o)}
              aria-expanded={kopfOffen}
              className="flex min-h-[56px] w-full items-center gap-3 px-4 text-left"
            >
              <span className="min-w-0 flex-1 truncate text-sm text-dark-text">
                <span className="text-dark-text-muted">Pilot</span> {pilotLabel || '—'}
                <span className="mx-2 text-dark-text-subtle">·</span>
                <span className="text-dark-text-muted">Einsatz</span> {ewListe.find(e => String(e.id) === einsatzId)?.titel ?? "–"}
              </span>
              <span className="text-sm font-medium text-[rgb(125_211_252)]">{kopfOffen ? 'Schließen' : 'Ändern'}</span>
              <ChevronDown className={`h-5 w-5 shrink-0 text-dark-text-muted transition-transform ${kopfOffen ? 'rotate-180' : ''}`} />
            </button>
            {kopfOffen && <div className="space-y-5 border-t border-dark-border p-4">

          {/* Pilot — Schloss neben der Auswahl */}
          <div>
            <label className="label">Pilot</label>
            {pilotGesperrt && user && (
              <p className="mb-1.5 flex items-start gap-1.5 text-sm text-dark-text-muted">
                <Lock className="mt-0.5 h-4 w-4 shrink-0" />Automatisch der eingeloggte Benutzer ({pilotLabel}). Über das Schloss entsperren, um zu wechseln.
              </p>
            )}
            <div className="relative mt-1.5">
              <div className="flex min-h-[48px] w-full items-center justify-between gap-3 rounded-lg border border-dark-border bg-dark-bg-tertiary/60 px-3 py-2.5 text-dark-text">
                <Select
                  value={pilotId || user?.id?.toString() || ''}
                  onChange={e => setPilotId(e.target.value)}
                  disabled={pilotGesperrt}
                  className="flex-1 bg-transparent border-none focus:ring-0 appearance-none text-dark-text cursor-pointer"
                  options={pilotList.map(p => ({ value: String(p.id), label: p.label }))}
                />
                <button
                  type="button"
                  onClick={() => setPilotGesperrt(g => !g)}
                  title={pilotGesperrt ? 'Pilot-Auswahl entsperren' : 'Pilot-Auswahl sperren'}
                  aria-label={pilotGesperrt ? 'Pilot-Auswahl entsperren' : 'Pilot-Auswahl sperren'}
                  className={`flex h-10 w-10 [@media(pointer:coarse)]:h-12 [@media(pointer:coarse)]:w-12 shrink-0 items-center justify-center rounded-lg transition-colors ${
                    pilotGesperrt ? 'text-primary-400 hover:bg-primary-400/10' : 'text-dark-text-muted hover:bg-dark-bg-tertiary'
                  }`}
                >
                  {pilotGesperrt ? <Lock className="h-4 w-4" /> : <LockOpen className="h-4 w-4" />}
                </button>
              </div>
            </div>
          </div>

          {/* Einsatz */}
          <div>
            <label className="label">Einsatz</label>
            {ewFehler && <p className="mb-1.5 text-xs text-red-300" role="alert">{ewFehler} Die Liste kann veraltet sein.</p>}
            {autoEinsatz && gesperrt && (
              <p className="mb-1.5 flex items-start gap-1.5 text-sm text-dark-text-muted">
                <Lock className="mt-0.5 h-4 w-4 shrink-0" />Automatisch aus dem Kräfte Management übernommen ({autoEinsatz.status}). Über das Schloss entsperren, um zu wechseln.
              </p>
            )}
            <div className="relative mt-1.5">
              <div className="flex min-h-[48px] w-full items-center justify-between gap-3 rounded-lg border border-dark-border bg-dark-bg-tertiary/60 px-3 py-2.5 text-dark-text">
                <Select
                  value={einsatzId}
                  onChange={e => setEinsatzLokal(e.target.value)}
                  disabled={gesperrt}
                  className="flex-1 bg-transparent border-none focus:ring-0 appearance-none text-dark-text cursor-pointer"
                  options={[
                    { value: '', label: '— Kein Einsatz —' },
                    ...einsaetze.map(x => ({ value: String(x.id), label: `${x.titel}${x.typ ? ` (${x.typ})` : ''}` })),
                  ]}
                />
                {autoEinsatz && (
                  <button
                    type="button"
                    onClick={() => {
                      if (gesperrt) { setGesperrt(false); return }
                      // wieder sperren: Einsatz aus dem Kräfte Management auch global übernehmen
                      setEinsatzLokal(null)
                      void setGlobalEinsatz(autoEinsatz!.id).then(ok => { if (ok) setGesperrt(true) })
                    }}
                    title={gesperrt ? 'Einsatz-Auswahl entsperren' : 'Automatische Auswahl wieder sperren'}
                    aria-label={gesperrt ? 'Einsatz-Auswahl entsperren' : 'Automatische Auswahl wieder sperren'}
                    className={`flex h-10 w-10 [@media(pointer:coarse)]:h-12 [@media(pointer:coarse)]:w-12 shrink-0 items-center justify-center rounded-lg transition-colors ${
                      gesperrt ? 'text-primary-400 hover:bg-primary-400/10' : 'text-dark-text-muted hover:bg-dark-bg-tertiary'
                    }`}
                  >
                    {gesperrt ? <Lock className="h-4 w-4" /> : <LockOpen className="h-4 w-4" />}
                  </button>
                )}
              </div>
            </div>
          </div>

            </div>}
          </div>

          {/* Drohne: große Kacheln, einsatz-zugeordnete zuerst mit ★ */}
          <div>
            <label className="label">Drohne *{vorbelegt.drohne && <span className="ml-2 text-sm font-normal text-[rgb(125_211_252)]">zuletzt verwendet</span>}</label>
            {drohnen.length === 0 ? (
              <p className="mt-1.5 rounded-xl border border-dark-border bg-dark-bg-tertiary/40 px-4 py-3 text-sm text-dark-text-muted">Keine Drohnen verfügbar.</p>
            ) : (
              <div className="mt-1.5 grid grid-cols-1 gap-2 min-[420px]:grid-cols-2" role="radiogroup" aria-label="Drohne">
                {drohnenOptionen.map(o => {
                  const aktiv = drohneId === o.value
                  const stern = o.label.startsWith('★')
                  return (
                    <button
                      key={o.value}
                      type="button"
                      role="radio"
                      aria-checked={aktiv}
                      onClick={() => waehleDrohne(o.value)}
                      className={`flex min-h-[64px] items-center gap-2 rounded-xl border px-4 py-2 text-left text-base font-semibold transition-colors ${
                        aktiv ? 'border-primary-500 bg-primary-500/20 text-dark-text ring-2 ring-primary-500/60' : 'border-dark-border bg-dark-bg-tertiary text-dark-text-muted hover:border-primary-500/60'
                      }`}
                    >
                      {stern && <Star className="h-4 w-4 shrink-0 fill-amber-300 text-amber-300" aria-label="diesem Einsatz zugeordnet" />}
                      <span className="min-w-0 flex-1 break-words">{stern ? o.label.replace(/^★\s*/, '') : o.label}</span>
                      {aktiv && <Check className="h-5 w-5 shrink-0 text-primary-300" />}
                    </button>
                  )
                })}
              </div>
            )}
            {einsatzDrohnenIds.length > 0 && <p className="mt-1.5 text-sm text-dark-text-muted">★ = diesem Einsatz zugeordnet</p>}
          </div>

          {/* Akku: Chips mit Kapazität */}
          <div>
            <label className="label">Akku{vorbelegt.akku && <span className="ml-2 text-sm font-normal text-[rgb(125_211_252)]">zuletzt verwendet</span>}</label>
            {!drohneId ? (
              <p className="mt-1.5 rounded-xl border border-dashed border-dark-border px-4 py-3 text-sm text-dark-text-muted">Zuerst Drohne wählen</p>
            ) : (
              <div className="mt-1.5 flex flex-wrap gap-2" role="radiogroup" aria-label="Akku">
                {akkuOptionen.map(o => {
                  const aktiv = akkuId === o.value
                  return (
                    <button
                      key={o.value}
                      type="button"
                      role="radio"
                      aria-checked={aktiv}
                      onClick={() => waehleAkku(o.value)}
                      className={`min-h-[56px] rounded-xl border px-4 py-2 text-left text-base font-semibold transition-colors ${
                        aktiv ? 'border-primary-500 bg-primary-500/20 text-dark-text ring-2 ring-primary-500/60' : 'border-dark-border bg-dark-bg-tertiary text-dark-text-muted hover:border-primary-500/60'
                      }`}
                    >
                      {aktiv && <Check className="mr-1.5 inline h-4 w-4 text-primary-300" />}{o.label}
                    </button>
                  )
                })}
              </div>
            )}
          </div>

          {/* Flugart */}
          <div>
            <label className="label">Flugart</label>
            <FlugartWahl value={flugart} onChange={waehleFlugart} />
          </div>

        </div>
      </Card>}

      {/* Fest unten sichtbar (im Scrollbereich über dem Dock der Shell): Flug starten */}
      {!formularZu && (
        <div className="sticky bottom-2 z-20 flex gap-3 rounded-xl border border-dark-border bg-dark-bg/95 p-2.5 shadow-lg backdrop-blur sm:bottom-0 sm:p-3">
          <Button
            onClick={() => starteFlug()}
            disabled={startet || !drohneId || !user?.id}
            className="!min-h-[56px] flex-1 text-base"
          >
            <Play className="h-5 w-5 mr-2" />
            {startet ? 'Starte Flug…' : !user?.id ? 'Benutzer fehlt' : !drohneId ? 'Drohne wählen' : 'Flug starten'}
          </Button>
          {anzeigeFluege.length > 0 && (
            <Button variant="secondary" onClick={() => setStartOffen(false)} className="!min-h-[56px] text-base">Zuklappen</Button>
          )}
        </div>
      )}

      {/* ===== Checklisten Slide-in Panel ===== */}
      {checklistenPanelOpen && currentChecklistenInstanz && (
        <div className="fixed inset-0 z-50 flex" onClick={closeChecklistenPanel}>
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
          <div className="relative z-10 flex-1 max-w-xl ml-auto bg-dark-bg rounded-2xl border border-dark-border flex flex-col h-full shadow-2xl animate-slide-in-right overflow-hidden">
            {/* Header */}
            <div className="flex h-16 shrink-0 items-center justify-between px-4 border-b border-dark-border bg-dark-bg-secondary rounded-t-2xl">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-400/20 text-primary-400">
                  {(PILOT_PHASES.find(p => p.key === checklistenPanelOpen)?.icon) && (() => {
                  const Icon = PILOT_PHASES.find(p => p.key === checklistenPanelOpen)!.icon;
                  return <Icon className="h-5 w-5" />;
                })()}
                </div>
                <div>
                  <h3 className="font-semibold text-dark-text">{PILOT_PHASES.find(p => p.key === checklistenPanelOpen)?.label}</h3>
                  <p className="text-xs text-dark-text-muted">{currentChecklistenInstanz.name}</p>
                </div>
              </div>
              <button onClick={closeChecklistenPanel} className="flex h-10 w-10 items-center justify-center rounded-lg text-dark-text-muted hover:bg-dark-bg-tertiary transition-colors">
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Progress */}
            <div className="px-4 py-3 border-b border-dark-border">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-dark-text">Fortschritt</span>
                <span className="text-sm font-mono text-primary-400">
                  {getErledigtCount(currentChecklistenInstanz)}/{currentChecklistenInstanz.items.filter(i => i.pflicht).length} Pflicht
                </span>
              </div>
              <div className="h-2 w-full bg-dark-border rounded-full overflow-hidden">
                <div 
                  className="h-full bg-primary-400 transition-all duration-300 rounded-full"
                  style={{ width: `${currentChecklistenInstanz.items.filter(i => i.pflicht).length > 0 ? (currentChecklistenInstanz.items.filter(i => i.pflicht && i.wert && i.wert !== '').length / currentChecklistenInstanz.items.filter(i => i.pflicht).length) * 100 : 0}%` }}
                />
              </div>
            </div>

            {/* Items */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {currentChecklistenInstanz.items.map((item: ChecklistenItem) => (
                <ChecklistZeile key={item.id} typ={item.typ} className="p-3 rounded-xl border border-dark-border bg-dark-bg-tertiary/50">
                  <div className="flex items-center gap-3">
                    {/* Input based on type */}
                    {item.typ === 'CHECK' && (
                      <input
                        type="checkbox"
                        checked={item.wert === 'true'}
                        onChange={e => updateChecklistenItem(currentChecklistenInstanz.id, item.id, e.target.checked ? 'true' : '')}
                        disabled={updatingItems[item.id]}
                        className="haken"
                      />
                    )}
                    {item.typ === 'TEXT' && (
                      <ChecklistenEingabe
                        type="text"
                        wert={item.wert || ''}
                        onSpeichern={(v) => updateChecklistenItem(currentChecklistenInstanz.id, item.id, v)}
                        className="flex-1 min-h-[48px] px-3 py-2 rounded border border-dark-border bg-dark-bg text-dark-text text-sm"
                        placeholder="Freitext..."
                      />
                    )}
                    {item.typ === 'NUMBER' && (
                      <ChecklistenEingabe
                        type="number"
                        wert={zahlWert(item.wert)}
                        onSpeichern={(v) => updateChecklistenItem(currentChecklistenInstanz.id, item.id, v)}
                        className="w-32 min-h-[48px] px-3 py-2 rounded border border-dark-border bg-dark-bg text-dark-text text-sm"
                        placeholder="Zahl"
                      />
                    )}
                    {item.typ === 'PHOTO' && (
                      <input
                        type="file"
                        accept="image/*"
                        onChange={e => { /* photo upload */ }}
                        disabled={updatingItems[item.id]}
                        className="min-h-[48px] px-3 py-2 rounded border border-dark-border bg-dark-bg text-dark-text text-sm"
                      />
                    )}

                    <div className="flex-1 min-w-0">
                      <p className={`font-medium text-sm ${item.pflicht ? 'text-dark-text' : 'text-dark-text-muted'}`}>
                        {item.text}
                        {item.pflicht && <span className="checklist-pflicht">Pflicht</span>}
                      </p>
                      {item.hinweis && <p className="text-xs text-dark-text-muted mt-0.5">{item.hinweis}</p>}
                      {item.wert && item.ausgefuellt_am && (
                        <p className="text-xs text-dark-text-muted mt-1">
                          Ausgefüllt: {new Date(item.ausgefuellt_am).toLocaleString('de-DE')}
                        </p>
                      )}
                    </div>
                  </div>
                </ChecklistZeile>
              ))}

              {currentChecklistenInstanz.status !== 'ABGESCHLOSSEN' && (
                <div className="sticky bottom-0 -mx-4 mt-4 space-y-2 border-t border-dark-border bg-dark-bg/95 px-4 py-3 backdrop-blur">
                {currentChecklistenInstanz.items.some(i => i.typ === 'CHECK' && i.pflicht && !i.wert) && (
                  <Button
                    variant="secondary"
                    onClick={async () => {
                      const inst = currentChecklistenInstanz
                      const offen = inst.items.filter(i => i.typ === 'CHECK' && i.pflicht && !i.wert)
                      if (!(await frageAlleOk(offen.length))) return
                      for (const i of offen) await updateChecklistenItem(inst.id, i.id, 'true')
                    }}
                    className="w-full min-h-[52px]"
                  >
                    Alle Pflichtpunkte ok
                  </Button>
                )}
                <Button
                  onClick={() => completeChecklistenInstanz(currentChecklistenInstanz.id)}
                  disabled={getErledigtCount(currentChecklistenInstanz) < currentChecklistenInstanz.items.filter(i => i.pflicht).length}
                  className="w-full min-h-[52px]"
                >
                  <CheckSquare className="h-5 w-5 mr-2" />
                  {getErledigtCount(currentChecklistenInstanz) === currentChecklistenInstanz.items.filter(i => i.pflicht).length && currentChecklistenInstanz.items.filter(i => i.pflicht).length > 0
                    ? 'Als abgeschlossen markieren'
                    : 'Bitte erst alle Pflichtfelder ausfüllen'}
                </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ===== VORFLUG-Checkliste (Dialog: am Handy Vollbild-Sheet) ===== */}
      <Dialog
        isOpen={vorflugModalOpen}
        onClose={cancelVorflug}
        title="Vorflug-Checkliste"
        maxWidth="xl"
        footer={<>
          <Button variant="secondary" onClick={cancelVorflug} className="min-h-[48px] min-w-[120px]">
                Abbrechen
              </Button>
          <Button variant="secondary" onClick={async () => { if (await frageAlleOk(vorflugItems.filter(i => i.typ === 'CHECK' && i.pflicht && !i.wert).length)) fillAllVorflugPflicht() }} className="min-h-[48px] min-w-[180px]">
                Alle Pflichtpunkte ok
              </Button>
          <Button variant="primary" onClick={confirmVorflug} className="min-h-[48px] min-w-[120px]">
                Bestätigen
              </Button>
        </>}
      >
        <p className="mb-4 text-sm text-dark-text-muted">Alle Pflichtfelder müssen vor dem Flugstart ausgefüllt sein</p>
            <div className="space-y-4">
              {vorflugItems.map((item, index) => (
                <ChecklistZeile key={item.id} typ={item.typ} className="p-4 rounded-xl border border-dark-border bg-dark-bg-tertiary/50">
                  <div className="flex items-center gap-3">
                    {item.typ === 'CHECK' && (
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          checked={item.wert === 'true'}
                          onChange={(e) => setVorflugItems(prev => 
                            prev.map(i => i.id === item.id ? { ...i, wert: e.target.checked ? 'true' : '' } : i)
                          )}
                          className="haken"
                        />
                      </div>
                    )}
                    {item.typ === 'TEXT' && (
                      <input
                        type="text"
                        value={item.wert || ''}
                        onChange={(e) => setVorflugItems(prev => 
                          prev.map(i => i.id === item.id ? { ...i, wert: e.target.value } : i)
                        )}
                        className="flex-1 px-3 py-2 rounded border border-dark-border bg-dark-bg text-dark-text text-base"
                        placeholder="Freitext..."
                      />
                    )}
                    {item.typ === 'NUMBER' && (
                      <input
                        type="number"
                        value={zahlWert(item.wert)}
                        onChange={(e) => setVorflugItems(prev => 
                          prev.map(i => i.id === item.id ? { ...i, wert: e.target.value } : i)
                        )}
                        className="w-32 px-3 py-2 rounded border border-dark-border bg-dark-bg text-dark-text text-base"
                        placeholder="Zahl"
                      />
                    )}
                    {item.typ === 'PHOTO' && (
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => { /* photo upload */ }}
                        className="px-3 py-2 rounded border border-dark-border bg-dark-bg text-dark-text text-sm"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className={`font-medium text-base ${item.pflicht ? 'text-dark-text' : 'text-dark-text-muted'}`}>
                        {item.text}
                        {item.pflicht && <span className="checklist-pflicht">Pflicht</span>}
                      </p>
                      {item.hinweis && <p className="text-sm text-dark-text-muted mt-1">{item.hinweis}</p>}
                    </div>
                  </div>
                </ChecklistZeile>
              ))}
            </div>

      </Dialog>
    </div>
  )
}

export default FlightCapturePage